import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import {
  API_EMPLOYEE_INSURANCE,
  API_EMPLOYEE_INSURANCE_STATS,
} from "../../../../../config/Endpoints.js";
import { ApiError, getApi } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import { showError } from "../../../../utils/toast.ts";
import type {
  InsuranceFilters,
  InsuranceRecord,
  InsuranceStats,
} from "../types.ts";

const statsSchema = z.object({
  totalInsuranceHolders: z.number().nonnegative(),
  pending: z.number().nonnegative(),
  approved: z.number().nonnegative().optional(),
  uploaded: z.number().nonnegative().optional(),
  revoked: z.number().nonnegative().optional(),
});

const recordSchema = z.object({
  id: z.number(),
  employeeId: z.number(),
  employeeName: z.string().nullish(),
  insuranceProviderName: z.string().nullish(),
  insuranceNumber: z.string().nullish(),
  insuranceFrom: z.string().nullish(),
  insuranceTo: z.string().nullish(),
  validityPeriod: z.number().nullish(),
  insuranceSoftCopy: z.string().nullish(),
  status: z.string().nullish(),
});

function message(cause: unknown, fallback: string): string {
  return cause instanceof ApiError ? cause.message : fallback;
}

function recordsUrl(filters: InsuranceFilters): string {
  const query = new URLSearchParams();
  if (filters.employeeId) query.set("employeeId", filters.employeeId);
  if (filters.status) query.set("status", filters.status);
  const suffix = query.toString();
  return suffix ? `${API_EMPLOYEE_INSURANCE}?${suffix}` : API_EMPLOYEE_INSURANCE;
}

export function useInsuranceStats() {
  const [stats, setStats] = useState<InsuranceStats>({
    totalInsuranceHolders: 0,
    pending: 0,
    approved: 0,
    revoked: 0,
  });
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const { data } = await getApi<unknown>(
        API_EMPLOYEE_INSURANCE_STATS,
        accessToken(),
        "json",
        signal,
      );
      const parsed = statsSchema.safeParse(data);
      if (!parsed.success) throw new Error("Invalid insurance stats response.");
      setStats({
        totalInsuranceHolders: parsed.data.totalInsuranceHolders,
        pending: parsed.data.pending,
        approved: parsed.data.approved ?? parsed.data.uploaded ?? 0,
        revoked: parsed.data.revoked ?? 0,
      });
    } catch (cause) {
      if (signal?.aborted) return;
      setStats({
        totalInsuranceHolders: 0,
        pending: 0,
        approved: 0,
        revoked: 0,
      });
      showError(message(cause, "Failed to load insurance statistics."));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void refetch(controller.signal);
    return () => controller.abort();
  }, [refetch]);

  return { stats, loading, refetch };
}

export function useInsuranceRecords(filters: InsuranceFilters) {
  const [records, setRecords] = useState<InsuranceRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      try {
        const { data } = await getApi<unknown>(
          recordsUrl(filters),
          accessToken(),
          "json",
          signal,
        );
        const payload =
          data && typeof data === "object" && "data" in data
            ? (data as { data?: unknown }).data
            : data;
        const parsed = z.array(recordSchema).safeParse(payload);
        if (!parsed.success) throw new Error("Invalid insurance records response.");
        setRecords(
          parsed.data.map((row) => ({
            id: row.id,
            employeeId: row.employeeId,
            employeeName: row.employeeName ?? "",
            insuranceProviderName: row.insuranceProviderName ?? "",
            insuranceNumber: row.insuranceNumber ?? "",
            insuranceFrom: row.insuranceFrom ?? "",
            insuranceTo: row.insuranceTo ?? "",
            validityPeriod: row.validityPeriod ?? 0,
            insuranceSoftCopy: row.insuranceSoftCopy ?? null,
            status: row.status ?? "",
          })),
        );
      } catch (cause) {
        if (signal?.aborted) return;
        setRecords([]);
        showError(message(cause, "Failed to load insurance records."));
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    const controller = new AbortController();
    void refetch(controller.signal);
    return () => controller.abort();
  }, [refetch]);

  return { records, loading, refetch };
}
